import { useState } from 'react';
import type { BanEntry } from '@/mocks/adminModeracion';

interface Props {
  bans: BanEntry[];
  onToggleActive: (id: string) => void;
  onExtend: (id: string) => void;
  onMakePermanent: (id: string) => void;
}

export default function BansTable({ bans, onToggleActive, onExtend, onMakePermanent }: Props) {
  const [search, setSearch] = useState('');
  const [actionMenu, setActionMenu] = useState<string | null>(null);

  const filtered = bans.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return b.username.toLowerCase().includes(q) || b.ip.includes(q) || b.reason.toLowerCase().includes(q);
  });

  const activeBans = bans.filter((b) => b.active);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Baneos activos', value: activeBans.length, icon: 'ri-forbid-2-line', color: 'text-red-500' },
          { label: 'Permanentes', value: activeBans.filter((b) => b.action === 'bloqueo_perm' || b.action === 'baneo_ip').length, icon: 'ri-close-circle-line', color: 'text-rose-500' },
          { label: 'Temporales', value: activeBans.filter((b) => b.action === 'bloqueo_temp').length, icon: 'ri-timer-line', color: 'text-amber-500' },
          { label: 'Total histórico', value: bans.length, icon: 'ri-history-line', color: 'text-foreground-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className={`${s.icon} ${s.color} text-sm`}></i>
              </div>
              <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar usuario, IP o motivo..."
          className="w-full pl-8 pr-8 py-2 rounded-xl bg-background-50 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors">
            <i className="ri-close-line text-background-50 text-[8px]"></i>
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Usuario</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden md:table-cell">IP</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Motivo</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden md:table-cell">Acción</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden lg:table-cell">Fecha</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Duración</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden lg:table-cell">Admin</th>
                <th className="text-center px-4 py-3 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
                        <i className="ri-user-search-line text-foreground-300 text-lg"></i>
                      </div>
                      <p className="text-xs text-foreground-400 font-medium">Sin resultados</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b.id} className={`border-b border-background-100 hover:bg-background-50 transition-colors ${!b.active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${b.active ? 'bg-red-500' : 'bg-foreground-300'}`}></div>
                        <span className="text-xs font-semibold text-foreground-900">{b.username}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <code className="text-[10px] text-foreground-500 bg-background-200 px-1.5 py-0.5 rounded font-mono">{b.ip}</code>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[10px] text-foreground-600 line-clamp-2 max-w-[200px]">{b.reason}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        b.action === 'bloqueo_perm'
                          ? 'bg-red-100 text-red-700'
                          : b.action === 'baneo_ip'
                            ? 'bg-rose-100 text-rose-700'
                            : 'bg-amber-100 text-amber-700'
                      }`}>
                        {b.action === 'bloqueo_perm' ? 'Permanente' : b.action === 'baneo_ip' ? 'Baneo IP' : 'Temporal'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-[10px] text-foreground-500">{formatDate(b.date)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-semibold text-foreground-600">{b.duration}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-[10px] text-foreground-500">{b.bannedBy}</span>
                    </td>
                    <td className="px-4 py-3 text-center relative">
                      <button
                        onClick={() => setActionMenu(actionMenu === b.id ? null : b.id)}
                        className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                      >
                        <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
                      </button>
                      {actionMenu === b.id && (
                        <div className="absolute right-2 top-10 z-30 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1.5 min-w-[170px]">
                          {b.active && (
                            <>
                              <button
                                onClick={() => { onToggleActive(b.id); setActionMenu(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-[10px] text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-check-line text-emerald-500 text-xs"></i>
                                Desbloquear
                              </button>
                              <button
                                onClick={() => { onMakePermanent(b.id); setActionMenu(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-[10px] text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-close-circle-line text-rose-500 text-xs"></i>
                                Convertir en permanente
                              </button>
                              {b.action === 'bloqueo_temp' && (
                                <button
                                  onClick={() => { onExtend(b.id); setActionMenu(null); }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-[10px] text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-add-circle-line text-amber-500 text-xs"></i>
                                  Extender bloqueo
                                </button>
                              )}
                            </>
                          )}
                          {!b.active && (
                            <button
                              onClick={() => { onToggleActive(b.id); setActionMenu(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-[10px] text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-forbid-2-line text-red-500 text-xs"></i>
                              Reactivar bloqueo
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}